import { useCallback, useEffect, useMemo } from "react";
import { buildRowCount } from "@/useCases/rowCount";
import { PageLayout } from "@/ui/layouts";
import { Card, CardContent, Loader, Pagination } from "@/ui/atoms";
import { EmptyState } from "@/ui/molecules";
import { FilesTable, narrowFileFacets, useFilesTableFilters } from "@/ui/organisms";
import { APAC_STATUS_LABELS } from "@/modules/rev_apac/statusLabels";
import { useServerTable, type TServerTableQueryInput } from "@/useCases/useServerTable";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import type { TAsyncState, TFileType, TFinancialFile, TPaginatedFiles } from "@/store";

/**
 * Configuration for a file list view variant.
 * Each variant differs only in page copy and optional structural scoping.
 */
export type TFilesListConfig = {
    title: string;
    description: string;
    errorTitle: string;
    emptyTitle: string;
    emptyDescription: string;
    hiddenColumns?: string[];
    availableFileTypes?: ReadonlySet<TFileType>;
    /** Archive page only: append the Reason and Archived columns. */
    showArchiveColumns?: boolean;
};

/**
 * Shared useCases contract consumed by all file-list view variants.
 */
export type TViewFilesListUseCases = {
    onMount: () => void;
    onUnmount: () => void;
    useFilesState: () => TAsyncState<TPaginatedFiles>;
    onFileClick: (file: TFinancialFile) => void;
    onRefresh: () => void;
    /** Server fetch wired to the current query. */
    onQuery: (input: TServerTableQueryInput, signal?: AbortSignal) => void;
    /** Facet options for Business Partner / Submission multiSelect columns. */
    useFacets: () => {
        businessPartnerOptions: { value: string; label: string }[];
        submissionOptions: { value: string; label: string; businessPartnerId?: string }[];
    };
    /** Called when access is denied; the wiring redirects to an accessible page. */
    onAccessDenied: () => void;
    /** When provided and false, the list redirects via onAccessDenied and does not load. */
    useCanAccess?: () => boolean;
    /** When provided and false, the table renders without column-filter controls. */
    useCanFilter?: () => boolean;
    /** When provided and false, rows are not clickable (read-only list). */
    useCanOpenFile?: () => boolean;
};

/**
 * View parameters (injected dependencies).
 */
export type TViewFilesListParams = {
    useCases: () => TViewFilesListUseCases;
};

/**
 * View props (from router/parent).
 */
export type TViewFilesListProps = Record<string, never>;

/**
 * Creates a parameterised file-list view.
 *
 * All three financial-file list pages (All Files, TB/GL, FinJournals) share the
 * same layout, loading/error/empty states, and table rendering.  The only
 * differences are the page title, empty-state copy, and structural server-side
 * scoping — all supplied via `config`.
 */
export const createViewFilesList =
    (config: TFilesListConfig) =>
    ({ useCases }: TViewFilesListParams) => {
        const ViewFilesList: React.FC<TViewFilesListProps> = () => {
            const {
                onMount,
                onUnmount,
                useFilesState,
                onFileClick,
                onRefresh,
                onQuery,
                useFacets,
                onAccessDenied,
                useCanAccess,
                useCanFilter,
                useCanOpenFile,
            } = useCases();
            const { initialColumnFilters, onColumnFiltersChange: persistToUrl } =
                useFilesTableFilters();
            const facets = useFacets();
            const canAccess = useCanAccess?.() ?? true;
            const canFilter = useCanFilter?.() ?? true;
            const canOpenFile = useCanOpenFile?.() ?? true;

            const filesState = useFilesState();

            const server = useServerTable<TServerTableQueryInput>({
                pageSize: 50,
                initialColumnFilters,
                toQuery: (input) => input,
                fetchData: onQuery,
            });

            const handleColumnFiltersChange = useCallback(
                (filters: typeof server.columnFilters) => {
                    persistToUrl(filters);
                    server.onColumnFiltersChange(filters);
                },
                // eslint-disable-next-line react-hooks/exhaustive-deps
                [persistToUrl, server.onColumnFiltersChange],
            );

            const narrowedFacets = useMemo(
                () => narrowFileFacets(facets, server.columnFilters),
                [facets, server.columnFilters],
            );

            useEffect(() => {
                if (!canAccess) return;
                onMount();
                return onUnmount;
            }, [onMount, onUnmount, canAccess]);

            useEffect(() => {
                if (!canAccess) onAccessDenied();
            }, [canAccess, onAccessDenied]);

            const files = filesState.data?.items;

            const renderContent = () => {
                if (!canAccess) {
                    return (
                        <div className="flex justify-center py-12">
                            <Loader size="lg" />
                        </div>
                    );
                }

                if (filesState.status === "pending" && !filesState.data) {
                    return (
                        <div className="flex justify-center py-12">
                            <Loader size="lg" />
                        </div>
                    );
                }

                if (filesState.status === "rejected") {
                    const stateError = (filesState as { error: Error | null }).error;
                    return (
                        <EmptyState
                            variant="error"
                            title={config.errorTitle}
                            description={extractErrorMessage(stateError)}
                            action={{ label: "Try Again", onClick: onRefresh }}
                        />
                    );
                }

                const hasActiveFilters = server.columnFilters.length > 0;
                if (!files?.length && !hasActiveFilters) {
                    return (
                        <EmptyState
                            title={config.emptyTitle}
                            description={config.emptyDescription}
                        />
                    );
                }

                return (
                    <>
                        <FilesTable
                            files={files ?? []}
                            statusLabels={APAC_STATUS_LABELS}
                            emptyMessage="No files match the current filters."
                            loading={filesState.status === "pending"}
                            onFileClick={canOpenFile ? onFileClick : undefined}
                            columnFilters={server.columnFilters}
                            onColumnFiltersChange={handleColumnFiltersChange}
                            sortingState={server.sorting}
                            onSortingChange={server.onSortingChange}
                            rowCount={buildRowCount({
                                total: filesState.data?.total ?? 0,
                                totalUnfiltered: filesState.data?.totalUnfiltered,
                                hasActiveFilters: server.columnFilters.length > 0,
                            })}
                            businessPartnerOptions={narrowedFacets.businessPartnerOptions}
                            submissionOptions={narrowedFacets.submissionOptions}
                            enableFilters={canFilter}
                            hiddenColumns={config.hiddenColumns}
                            availableFileTypes={config.availableFileTypes}
                            showArchiveColumns={config.showArchiveColumns}
                            storageKey={`files-${config.title}`}
                        />
                        <Pagination
                            page={server.page}
                            pageSize={filesState.data?.pageSize ?? 50}
                            total={filesState.data?.total ?? 0}
                            onPageChange={server.setPage}
                            className="mt-4"
                        />
                    </>
                );
            };

            return (
                <PageLayout title={config.title} description={config.description}>
                    <Card>
                        {/* No horizontal padding so the table lines up with the page
                            header (title/description) and uses the full content width. */}
                        <CardContent className="px-0 pt-6">{renderContent()}</CardContent>
                    </Card>
                </PageLayout>
            );
        };

        return ViewFilesList;
    };
